import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, Check, Layers, Search } from "lucide-react";

export default function CouponBatches() {
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({ redemption_limit: 5, discount_percent: 20 });
  const [generating, setGenerating] = useState(false);
  const [activeBatch, setActiveBatch] = useState(null);
  const [codes, setCodes] = useState([]);
  const [searchCode, setSearchCode] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  const loadBatches = () => api.get("/admin/coupons/batches").then(r => setBatches(r.data));
  useEffect(() => { loadBatches(); }, []);

  const generate = async () => {
    if (!form.redemption_limit || !form.discount_percent) {
      toast.error("Set both a redemption limit and a discount percent first");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/coupons/batches", { ...form, count: 100 });
      toast.success(`Batch #${data.batch_number} — 100 codes generated`);
      setForm({ redemption_limit: "", discount_percent: "" });
      loadBatches();
      openBatch({ id: data.batch_id, batch_number: data.batch_number });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setGenerating(false);
    }
  };

  const openBatch = async (b) => {
    setActiveBatch(b);
    const { data } = await api.get(`/admin/coupons/batches/${b.id}/codes`);
    setCodes(data);
  };

  const copyCode = async (code) => {
    try {
      await api.post(`/admin/coupons/${code}/copy`);
      await navigator.clipboard.writeText(code);
      setCodes(cs => cs.map(c => c.code === code ? { ...c, copied: true } : c));
      toast.success(`${code} copied`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const search = async () => {
    if (!searchCode.trim()) return;
    try {
      const { data } = await api.get("/admin/coupons/search", { params: { code: searchCode.trim() } });
      setSearchResult(data);
    } catch (e) {
      setSearchResult(null);
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="coupon-batches">
      <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Layers className="w-6 h-6" /> Random Coupon Code Generation</h2>
      <p className="text-slate-500 text-sm mb-6">Generate a batch of 100 unique 6-digit codes at once, sharing one redemption limit and discount.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4">New batch</h3>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Redemption limit (per code)</Label>
            <Input type="number" min="1" value={form.redemption_limit}
              onChange={e => setForm({ ...form, redemption_limit: parseInt(e.target.value || "1") })} data-testid="batch-limit-input" />
          </div>
          <div>
            <Label>Discount % (on base value)</Label>
            <Input type="number" min="1" max="100" value={form.discount_percent}
              onChange={e => setForm({ ...form, discount_percent: parseFloat(e.target.value || "0") })} data-testid="batch-discount-input" />
          </div>
          <Button onClick={generate} disabled={generating} className="bg-black hover:bg-slate-800 text-white" data-testid="generate-batch-btn">
            {generating ? "Generating..." : "Generate 100 Codes"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Search a coupon code</h3>
        <div className="flex gap-2 max-w-md">
          <Input placeholder="e.g. 482913" value={searchCode} onChange={e => setSearchCode(e.target.value)} data-testid="coupon-search-input" />
          <Button variant="outline" onClick={search}>Search</Button>
        </div>
        {searchResult && (
          <div className="mt-4 text-sm bg-slate-50 rounded-lg p-4 space-y-1">
            <div><b>Code:</b> {searchResult.coupon.code} · <b>Source:</b> {searchResult.coupon.source}</div>
            <div><b>Discount:</b> {searchResult.coupon.discount_value}% · <b>Uses:</b> {searchResult.coupon.used_count}/{searchResult.coupon.max_uses ?? "∞"}</div>
            {searchResult.batch && <div><b>Batch:</b> #{searchResult.batch.batch_number}</div>}
            <div><b>Linked company:</b> {searchResult.company ? `${searchResult.company.name} (${searchResult.company.company_id})` : "Not yet redeemed"}</div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4 h-fit">
          <h3 className="font-semibold text-sm mb-3">Batch history</h3>
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {batches.length === 0 ? (
              <p className="text-xs text-slate-400">No batches yet.</p>
            ) : batches.map(b => (
              <button
                key={b.id}
                onClick={() => openBatch(b)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${activeBatch?.id === b.id ? "bg-black text-white" : "hover:bg-slate-50 text-slate-700"}`}
                data-testid={`batch-${b.batch_number}`}
              >
                <div className="font-medium">Batch #{b.batch_number}</div>
                <div className={`text-xs ${activeBatch?.id === b.id ? "text-white/70" : "text-slate-400"}`}>
                  {b.discount_percent}% · {b.redemption_limit}x · {b.copied_count}/{b.codes_count} copied
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          {!activeBatch ? (
            <p className="text-sm text-slate-400 text-center py-12">Select a batch to view its codes.</p>
          ) : (
            <>
              <h3 className="font-semibold text-sm mb-3">Batch #{activeBatch.batch_number} codes</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto">
                {codes.map(c => (
                  <button
                    key={c.code}
                    onClick={() => !c.copied && copyCode(c.code)}
                    disabled={c.copied}
                    data-testid={`copy-code-${c.code}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border font-mono text-sm transition-colors ${
                      c.copied
                        ? "bg-slate-50 border-slate-100 text-slate-300 line-through cursor-not-allowed"
                        : "border-slate-200 text-slate-800 hover:border-black hover:bg-slate-50"
                    }`}
                  >
                    {c.code}
                    {c.copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
